import React, { useEffect, useState, useCallback } from 'react';
import { studentApi } from '../../services/api';

interface ExamItem {
  id: string;
  name: string;
  location: string | null;
  exam_date: string;
  registration_deadline: string;
  credit_cost: number;
  capacity: number | null;
  registered_count: number;
  institution: { id: number; name: string } | null;
  notes: string | null;
  is_registered: boolean;
  is_full: boolean;
  deadline_passed: boolean;
  is_past?: boolean;
}

const F = { fontFamily: 'Inter' } as const;

function fmtDate(s: string) {
  return new Date(s).toLocaleString('tr-TR', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const StudentExamAppointments: React.FC = () => {
  const [balance, setBalance] = useState<number | null>(null);
  const [exams, setExams] = useState<ExamItem[]>([]);
  const [pastRegistrations, setPastRegistrations] = useState<ExamItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [confirmExam, setConfirmExam] = useState<ExamItem | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    studentApi.examAppointments()
      .then((res: any) => {
        setBalance(res.data?.balance ?? 0);
        setExams(res.data?.exams ?? []);
        setPastRegistrations(res.data?.past_registrations ?? []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRegister = async (exam: ExamItem) => {
    setConfirmExam(null);
    setRegistering(exam.id);
    setMsg(null);
    try {
      const res: any = await studentApi.registerExamAppointment(exam.id);
      const used = res.data?.credit_used ?? 0;
      setMsg({ kind: 'ok', text: `${exam.name} sınavına kayıt olundu.${used > 0 ? ` ${used} kredi kullanıldı.` : ''}` });
      load();
    } catch (e: any) {
      setMsg({ kind: 'err', text: e?.response?.data?.error ?? 'Kayıt başarısız.' });
    } finally {
      setRegistering(null);
    }
  };

  const handleCancel = async (exam: ExamItem) => {
    if (!window.confirm(`"${exam.name}" kaydınızı iptal etmek istiyor musunuz?${exam.credit_cost > 0 ? ` ${exam.credit_cost} kredi iade edilecek.` : ''}`)) return;
    setCancelling(exam.id);
    setMsg(null);
    try {
      const res: any = await studentApi.cancelExamRegistration(exam.id);
      const refunded = res.data?.refunded ?? 0;
      setMsg({ kind: 'ok', text: `Kayıt iptal edildi.${refunded > 0 ? ` ${refunded} kredi iade edildi.` : ''}` });
      load();
    } catch (e: any) {
      setMsg({ kind: 'err', text: e?.response?.data?.error ?? 'İptal başarısız.' });
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Deneme Sınavları</h1>
          <p style={{ ...F, fontSize: 14, color: '#888', marginTop: 4 }}>Yaklaşan deneme sınavı randevularını görüntüleyin ve kayıt olun.</p>
        </div>
        {balance !== null && (
          <div className="flex items-center gap-2 bg-[#fafafa] rounded-[16px] px-5 py-3" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            {/* Üst bar ile aynı Digipo kredi ikonu (#27) — farklı krediler olduğu sanılmasın diye 💳 emoji yerine paylaşılan asset. */}
            <img src="/new-icons/LMS 1.svg" alt="" className="w-7 h-7 object-contain" />
            <div>
              <p style={{ ...F, fontSize: 11, color: '#888' }}>Kredi Bakiyeniz</p>
              <p style={{ ...F, fontSize: 20, fontWeight: 700, color: '#3973bf' }}>{balance}</p>
            </div>
          </div>
        )}
      </div>

      {msg && (
        <div
          className="mb-5 rounded-[12px] px-5 py-3"
          style={{
            background: msg.kind === 'ok' ? '#eafbea' : '#fff0f0',
            border: `1.5px solid ${msg.kind === 'ok' ? '#11D700' : '#e5599b'}`,
          }}
        >
          <p style={{ ...F, fontSize: 14, color: msg.kind === 'ok' ? '#27a745' : '#e5599b', fontWeight: 600 }}>
            {msg.kind === 'ok' ? '✓ ' : '✗ '}{msg.text}
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : exams.length === 0 ? (
        <div className="bg-[#fafafa] rounded-[20px] p-10 text-center" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <p style={{ ...F, fontSize: 40, marginBottom: 12 }}>📋</p>
          <p style={{ ...F, fontSize: 18, fontWeight: 600, color: '#2b2b2b', marginBottom: 8 }}>Deneme Sınavı Yok</p>
          <p style={{ ...F, fontSize: 14, color: '#888' }}>Şu anda kayıt açık deneme sınavı bulunmuyor.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {exams.map(exam => {
            const canRegister = !exam.is_registered && !exam.is_full && !exam.deadline_passed;
            const hasEnoughCredits = balance !== null ? balance >= exam.credit_cost : true;
            const isProcessing = registering === exam.id || cancelling === exam.id;

            return (
              <div
                key={exam.id}
                className="bg-[#fafafa] rounded-[20px] p-6"
                style={{
                  boxShadow: '0px 1px 4px rgba(0,0,0,0.12)',
                  border: exam.is_registered ? '2px solid #11D700' : '2px solid transparent',
                }}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h2 style={{ ...F, fontSize: 20, fontWeight: 700, color: '#2b2b2b' }}>{exam.name}</h2>
                      {exam.is_registered && (
                        <span className="rounded-full px-3 py-1 text-[12px] font-bold text-white" style={{ background: '#11D700' }}>
                          ✓ Kayıtlısınız
                        </span>
                      )}
                      {exam.is_full && !exam.is_registered && (
                        <span className="rounded-full px-3 py-1 text-[12px] font-bold text-white" style={{ background: '#e5599b' }}>
                          Kontenjan Doldu
                        </span>
                      )}
                      {exam.deadline_passed && !exam.is_registered && (
                        <span className="rounded-full px-3 py-1 text-[12px] font-bold" style={{ background: '#f5f5f5', color: '#888' }}>
                          Kayıt Kapalı
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[14px]" style={F}>
                      <p style={{ color: '#2b2b2b' }}>
                        <span style={{ color: '#3973bf', fontWeight: 600 }}>📅 Sınav:</span> {fmtDate(exam.exam_date)}
                      </p>
                      <p style={{ color: '#2b2b2b' }}>
                        <span style={{ color: '#3973bf', fontWeight: 600 }}>⏰ Son Kayıt:</span> {fmtDate(exam.registration_deadline)}
                      </p>
                      {exam.location && (
                        <p style={{ color: '#2b2b2b' }}>
                          <span style={{ color: '#3973bf', fontWeight: 600 }}>📍 Yer:</span> {exam.location}
                        </p>
                      )}
                      {exam.institution && (
                        <p style={{ color: '#2b2b2b' }}>
                          <span style={{ color: '#3973bf', fontWeight: 600 }}>🏫 Kurum:</span> {exam.institution.name}
                        </p>
                      )}
                      <p style={{ color: '#2b2b2b' }}>
                        <span style={{ color: '#3973bf', fontWeight: 600 }}>👥 Kayıtlı:</span>{' '}
                        {exam.registered_count}{exam.capacity !== null ? `/${exam.capacity}` : ''} kişi
                      </p>
                      <p
                        style={{ color: exam.credit_cost > 0 ? '#e5599b' : '#27a745', fontWeight: 600 }}
                        className="flex items-center gap-1.5"
                      >
                        {exam.credit_cost > 0 ? (
                          <>
                            <img src="/new-icons/LMS 1.svg" alt="" className="w-4 h-4 object-contain" />
                            {exam.credit_cost} kredi
                          </>
                        ) : (
                          'Ücretsiz'
                        )}
                      </p>
                    </div>

                    {exam.notes && (
                      <p className="mt-3 text-[13px] text-gray-500 italic">{exam.notes}</p>
                    )}

                    {canRegister && exam.credit_cost > 0 && !hasEnoughCredits && (
                      <p className="mt-2 text-[13px] font-semibold" style={{ color: '#e5599b' }}>
                        ⚠ Yetersiz kredi — Gerekli: {exam.credit_cost}, Bakiyeniz: {balance}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 flex-shrink-0 min-w-[160px]">
                    {exam.is_registered ? (
                      <button
                        onClick={() => handleCancel(exam)}
                        disabled={isProcessing}
                        className="rounded-[12px] px-5 py-3 font-bold text-[14px] hover:opacity-90 disabled:opacity-50 transition-opacity"
                        style={{ background: '#fafafa', border: '2px solid #e5599b', color: '#e5599b', ...F }}
                      >
                        {cancelling === exam.id ? 'İptal ediliyor…' : 'Kaydı İptal Et'}
                      </button>
                    ) : canRegister ? (
                      <button
                        onClick={() => exam.credit_cost > 0 ? setConfirmExam(exam) : handleRegister(exam)}
                        disabled={isProcessing || !hasEnoughCredits}
                        className="rounded-[12px] px-5 py-3 font-bold text-[14px] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
                        style={{ background: hasEnoughCredits ? '#3973bf' : '#c5c5c5', ...F }}
                      >
                        {registering === exam.id ? 'Kayıt yapılıyor…' : 'Kayıt Ol'}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Geçmiş Kayıtlar */}
      {pastRegistrations.length > 0 && (
        <div className="mt-8">
          <h2 style={{ ...F, fontSize: 20, fontWeight: 600, color: '#2b2b2b', marginBottom: 12 }}>
            Geçmiş Sınav Kayıtlarım
          </h2>
          <div className="space-y-3">
            {pastRegistrations.map(exam => (
              <div
                key={exam.id}
                className="bg-[#fafafa] rounded-[16px] p-4 flex items-center gap-4"
                style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.08)', opacity: 0.75 }}
              >
                <div className="flex-1 min-w-0">
                  <p style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b' }}>{exam.name}</p>
                  <div className="flex gap-4 text-[13px] text-gray-500 flex-wrap mt-1" style={F}>
                    <span>📅 {fmtDate(exam.exam_date)}</span>
                    {exam.location && <span>📍 {exam.location}</span>}
                    {exam.institution && <span>🏫 {exam.institution.name}</span>}
                  </div>
                </div>
                <span className="rounded-full px-3 py-1 text-[12px] font-bold" style={{ background: '#f5f5f5', color: '#888', ...F }}>
                  Tamamlandı
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Kredi onay popup */}
      {confirmExam && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setConfirmExam(null)} />
          <div className="relative bg-white rounded-[20px] w-full max-w-[440px] p-7 text-center" style={{ boxShadow: '0px 8px 24px rgba(0,0,0,0.2)' }}>
            <img src="/new-icons/LMS 1.svg" alt="" className="w-12 h-12 mx-auto mb-3 object-contain" />
            <h2 style={{ ...F, fontSize: 20, fontWeight: 700, color: '#2b2b2b', marginBottom: 8 }}>Kredi Kullanımı Onayı</h2>
            <p style={{ ...F, fontSize: 15, color: '#2b2b2b', marginBottom: 8 }}>
              <strong>{confirmExam.name}</strong> sınavına kayıt için <strong style={{ color: '#e5599b' }}>{confirmExam.credit_cost} kredi</strong> kullanılacak.
            </p>
            <p style={{ ...F, fontSize: 14, color: '#888', marginBottom: 20 }}>
              Mevcut bakiyeniz: {balance} kredi → Kayıt sonrası: {(balance ?? 0) - confirmExam.credit_cost} kredi
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmExam(null)}
                className="flex-1 rounded-[12px] py-3 font-semibold text-[15px] border border-gray-200 hover:bg-gray-50"
                style={F}
              >
                Vazgeç
              </button>
              <button
                onClick={() => handleRegister(confirmExam)}
                className="flex-1 rounded-[12px] py-3 font-bold text-[15px] text-white hover:opacity-90"
                style={{ background: '#3973bf', ...F }}
              >
                Onayla ve Kayıt Ol
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentExamAppointments;
